import { useCallback, useEffect, useRef, useState } from "react";

export type ExecuteRecaptchaReturnType = {
  reCaptchaToken: string;
  reCaptchaAction: string;
};

type RecaptchaHookReturn = {
  executeRecaptcha: (action: string) => Promise<ExecuteRecaptchaReturnType>;
  isReady: boolean;
};

const loadRecaptchaScript = (siteKey: string): Promise<void> => {
  return new Promise((resolve) => {
    // If already loaded, resolve immediately
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => resolve());
      return;
    }

    // Create script element
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha.ready(() => resolve());
    };

    document.head.appendChild(script);
  });
};

const useRecaptchaV3 = (
  siteKey: string,
  isRecaptchaEnabled: boolean,
  shouldLoadScript = true, // pass false if you want to load script on click or scroll
): RecaptchaHookReturn => {
  const [isRecaptchaReady, setIsRecaptchaReady] = useState<boolean>(false);
  const loadingPromise = useRef<Promise<void> | null>(null);

  // Preload ReCAPTCHA when the hook is mounted

  const setupRecaptcha = useCallback(() => {
    if (isRecaptchaEnabled && !isRecaptchaReady) {
      loadingPromise.current = loadRecaptchaScript(siteKey);
      loadingPromise.current.then(() => {
        setIsRecaptchaReady(true);
      });
      document.removeEventListener("scroll", setupRecaptcha);
      document.body.removeEventListener("click", setupRecaptcha, true);
    }
  }, [isRecaptchaEnabled, siteKey]);

  useEffect(() => {
    document.addEventListener("scroll", setupRecaptcha);
    document.body.addEventListener("click", setupRecaptcha, true);
    if (shouldLoadScript) {
      setupRecaptcha();
    }
    return () => {
      document.removeEventListener("scroll", setupRecaptcha);
      document.body.removeEventListener("click", setupRecaptcha, true);
    };
  }, []);

  const executeRecaptcha = useCallback(
    async (action: string): Promise<ExecuteRecaptchaReturnType> => {
      try {
        // Wait for ReCAPTCHA to be ready if it's not
        if (!isRecaptchaReady && loadingPromise.current) {
          await loadingPromise.current;
        }

        if (!window.grecaptcha) {
          throw new Error("ReCAPTCHA not available");
        }

        const reCaptchaToken = await window.grecaptcha.execute(siteKey, {
          action,
        });
        return {
          reCaptchaToken,
          reCaptchaAction: action,
        };
      } catch (error) {
        console.error("ReCAPTCHA execution error:", error);
        throw error;
      }
    },
    [siteKey, isRecaptchaReady],
  );

  return {
    executeRecaptcha,
    isReady: isRecaptchaReady,
  };
};

export default useRecaptchaV3;
